import React from 'react';
import { Box, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

/**
 * "You don't have access to view X" — the block a screen is replaced by (INV-108).
 *
 * ⚠ NO ACTION ON IT, deliberately. The behaviour this replaces was a page that mounted, read
 * nothing, and offered "Add client" — a form that fills in and is refused on submit. A button
 * here (Request access, Go back, Retry) would be the same mistake in a politer font: nothing the
 * web can do changes the answer, and the sentence already names who can — an owner or admin, in
 * Settings > Team members.
 *
 * The SENTENCE is the caller's, from `auth/screenAccess.ts`, so the refusal and the
 * "we couldn't check" copy share one calm block instead of two look-alikes drifting apart.
 * Styling follows the Reports page's in-place message (ADR-0035 A8): a quiet band, not an alert —
 * this is a setting, not an error.
 */
export interface NoAccessProps {
  message: string;
  /**
   * INSIDE A TAB OR A CARD, NOT ON A WHOLE ROUTE (the patient chart, 2026-09-21).
   *
   * The full block is sized for a page that is otherwise empty — 10rem of vertical margin and a
   * 32px lock, which is right when the sentence IS the screen. A chart panel is one section of a
   * page whose header, rail and other tabs are all still on screen, and at full size the block
   * pushed the rest of the chart out of view and read as an error that had replaced the page.
   * Compact keeps the same band, the same colours and the SAME SENTENCE — only the paddings and
   * the icon shrink — so the two never drift into looking like two different refusals.
   */
  dense?: boolean;
}

const NoAccess: React.FC<NoAccessProps> = ({ message, dense = false }) => (
  <Box
    data-testid="no-access"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: dense ? 1 : 1.5,
      maxWidth: 520,
      mx: 'auto',
      my: dense ? 3 : { xs: 6, sm: 10 },
      px: dense ? 2 : 3,
      py: dense ? 3 : 5,
      textAlign: 'center',
      bgcolor: '#FFFFFF',
      border: '1px solid #E8DFD3',
      borderRadius: '8px',
    }}
  >
    <LockOutlinedIcon aria-hidden sx={{ fontSize: dense ? 24 : 32, color: '#A0744D' }} />
    <Typography sx={{ fontSize: dense ? '0.875rem' : '0.95rem', color: '#6B5B4B', lineHeight: 1.6 }}>
      {message}
    </Typography>
  </Box>
);

export default NoAccess;
